import { TextField } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import {
  decimalMinutesToMmSs,
  handleValueChange,
  isValidDuration,
  minutesToTime,
  validateAndFormatTimeOnBlur,
} from "../../../utils/durationHelpers";

interface DurationInputProps {
  initMinutes?: number;
  disabled?: boolean;
  label?: string;
  onTimeChange?: (time: string) => void;
  onFocusHandler?: React.FocusEventHandler<HTMLInputElement>;
  readOnly?: boolean;
  mode?: "hh:mm" | "mm:ss";
}

const DurationInput: React.FC<DurationInputProps> = ({
  initMinutes = 0,
  disabled = false,
  onTimeChange,
  label,
  onFocusHandler,
  readOnly = false,
  mode = "hh:mm",
}) => {
  const [time, setTime] = useState("");
  const [error, setError] = useState(false);
  const [helperText, setHelperText] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const [lastVal, setLastVal] = useState("");

  // Initialize time when component mounts or initMinutes changes
  useEffect(() => {
    const initialTime =
      mode === "mm:ss"
        ? decimalMinutesToMmSs(initMinutes)
        : minutesToTime(initMinutes);
    setTime(initialTime);
  }, [initMinutes, mode]);

  const onChangeHandler = (val: string) => {
    if (val === time) {
      return;
    }
    if (isValidDuration(val)) {
      const newVal = handleValueChange(val, lastVal);
      setLastVal(newVal);
      setTime(newVal);
      setError(false);
      setHelperText("");
    }
  };

  const onBlur = () => {
    const { formattedTime, error, helperText } =
      validateAndFormatTimeOnBlur(time);

    setTime(formattedTime || time);
    setError(error);
    setHelperText(helperText);

    if (!error && formattedTime) {
      onTimeChange?.(formattedTime);
    }
  };

  const handleClick = () => {
    const input = inputRef.current;
    if (!input || !time.includes(":")) return;
    const pos = input.selectionStart ?? 0;
    const colonIndex = time.indexOf(":");
    requestAnimationFrame(() => {
      if (pos <= colonIndex) {
        input.setSelectionRange(0, colonIndex);
      } else {
        input.setSelectionRange(colonIndex + 1, time.length);
      }
    });
  };

  return (
    <TextField
      label={label}
      type="tel"
      disabled={disabled}
      placeholder=" "
      value={time}
      onChange={(e) => onChangeHandler(e.target.value)}
      onFocus={onFocusHandler}
      onClick={handleClick}
      onBlur={onBlur}
      inputRef={inputRef}
      variant="outlined"
      fullWidth
      error={error}
      helperText={helperText}
      slotProps={{
        input: {
          readOnly,
        },
      }}
    />
  );
};

export default DurationInput;
